import { useEffect, useRef, useState } from "react";
import {
  useGetDatasetQuestionsQuery,
  useLazyGetDatasetQuestionsQuery,
} from "@store/api/dataset-api";
import type { DatasetQuestionsSearchResponse } from "@models/Dataset";
import { useDebounce } from "@hooks/use-debounce";

const BASE_QUESTION_TYPES = ["SINGLE", "MULTI"];
const PAGE_SIZE = 20;

/**
 * Searchable, paginated list of Single/Multiple/Single-grid questions for a
 * dataset, used by the Base Condition question selector. Single grid is not a
 * distinct API type — it's a "single" question with more than one variable —
 * so no extra type filtering is needed beyond SINGLE/MULTI.
 *
 * The unsearched first page is fetched via a plain query rather than a lazy
 * trigger: every condition row asks for the exact same "page 1, no search"
 * args, and a plain query subscription lets RTK Query share one cached
 * network request across all of them. A lazy trigger's `trigger()` always
 * starts a new request even if an identical one is already cached, which
 * previously meant one duplicate request per row. Search results and
 * pagination beyond page 1 are row-specific and stay on the lazy trigger.
 */
export function useBaseQuestionSearch(datasetId?: string | null) {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const isSearching = debouncedSearchTerm.length > 0;

  const { data: defaultPageData, isFetching: isFetchingDefault } =
    useGetDatasetQuestionsQuery(
      {
        id: datasetId as string,
        page: 1,
        page_size: PAGE_SIZE,
        question_type: BASE_QUESTION_TYPES,
      },
      { skip: !datasetId || isSearching },
    );

  const [extraQuestions, setExtraQuestions] = useState<DatasetQuestionsSearchResponse[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  const [triggerFetch, { isFetching: isFetchingExtra }] =
    useLazyGetDatasetQuestionsQuery();

  const fetchPage = async (page: number, isNewSearch: boolean) => {
    if (!datasetId) return;
    try {
      const result = await triggerFetch({
        id: datasetId,
        page,
        page_size: PAGE_SIZE,
        question_type: BASE_QUESTION_TYPES,
        search_query: debouncedSearchTerm || undefined,
      }).unwrap();

      const newQuestions = result?.questions || [];
      setExtraQuestions((prev) => {
        if (isNewSearch) return newQuestions;
        const existingIds = new Set(prev.map((q) => q.datasetElementId));
        return [
          ...prev,
          ...newQuestions.filter((q) => !existingIds.has(q.datasetElementId)),
        ];
      });
      setHasMore(result?.pagination?.hasNextPage || false);
      setCurrentPage(page);
    } catch {
      setHasMore(false);
    }
  };

  // Toggling search resets pagination. While searching, page 1 goes through
  // the lazy trigger (search results are row-specific, not shared). Clearing
  // the search drops the row back to the shared default page.
  useEffect(() => {
    if (!datasetId) return;
    setCurrentPage(1);
    if (isSearching) {
      setHasMore(true);
      fetchPage(1, true);
    } else {
      setExtraQuestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId, debouncedSearchTerm]);

  // Keep hasMore in sync with the shared default page while not searching.
  useEffect(() => {
    if (!isSearching) {
      setHasMore(defaultPageData?.pagination?.hasNextPage ?? false);
    }
  }, [isSearching, defaultPageData]);

  // The Autocomplete listbox (and observerTarget) only mounts once the dropdown
  // opens, so poll briefly until it's available before wiring the observer.
  useEffect(() => {
    if (!datasetId) return;

    const isFetching = isSearching
      ? isFetchingExtra
      : isFetchingDefault || isFetchingExtra;

    let observer: IntersectionObserver | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const setupObserver = () => {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isFetching) {
            fetchPage(currentPage + 1, false);
          }
        },
        { threshold: 0.1 },
      );
      if (observerTarget.current) {
        observer.observe(observerTarget.current);
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    intervalId = setInterval(() => {
      if (observerTarget.current) setupObserver();
    }, 100);
    if (observerTarget.current) setupObserver();

    return () => {
      if (intervalId) clearInterval(intervalId);
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isFetchingDefault, isFetchingExtra, currentPage, datasetId, isSearching]);

  const questions = isSearching
    ? extraQuestions
    : [...(defaultPageData?.questions ?? []), ...extraQuestions];

  const isFetching = isSearching
    ? isFetchingExtra
    : isFetchingDefault || isFetchingExtra;

  return {
    questions,
    searchTerm,
    setSearchTerm,
    hasMore,
    isFetching,
    observerTarget,
  };
}
